import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, UserPlus, Trash2, Lock, Users, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "./ConfirmDialog";

/**
 * AdminUsersCard — manage the admin allowlist from /admin/settings.
 *
 * Two buckets:
 *   • from_env: locked (set via ADMIN_EMAILS env var). Cannot be removed via
 *     the UI — must update env + redeploy.
 *   • from_db: managed here.
 *
 * Magic-link sign-in checks the union of both buckets — adding an email here
 * grants admin access immediately on their next magic-link request.
 */
export default function AdminUsersCard() {
  const [data, setData] = useState({ from_env: [], from_db: [], current: "" });
  const [loading, setLoading] = useState(true);
  const [addEmail, setAddEmail] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load admin users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const email = addEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true);
    try {
      const r = await api.post("/admin/users", { email, send_invite: sendInvite });
      if (r.data.noop) {
        toast.info(r.data.reason === "already_set_via_env" ? "Already set via env (locked)" : `Already in the list${r.data.invite_sent ? " · invite re-sent" : ""}`);
      } else if (r.data.invite_sent) {
        toast.success(`${email} added & invitation email sent`);
      } else {
        toast.success(`${email} added (no invite sent)`);
      }
      setAddEmail("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally { setAdding(false); }
  };

  const remove = async (email) => {
    setRemovingEmail(email);
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      toast.success(`${email} removed`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally { setRemovingEmail(null); }
  };

  // Re-send invite using the same POST endpoint — it queues a fresh invite
  // even when the email is already in the allowlist (returns noop:true,
  // invite_sent:true). Works for env admins too since they're also valid
  // sign-in targets, even though they can't be added to the DB list.
  const resend = async (email) => {
    setResendingEmail(email);
    try {
      const r = await api.post("/admin/users", { email, send_invite: true });
      if (r.data.invite_sent) {
        toast.success(`Invitation re-sent to ${email}`);
      } else {
        toast.info("Invite was not queued — check SMTP / email config");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally { setResendingEmail(null); }
  };

  return (
    <Card className="bg-white border-slate-200 p-6 shadow-sm" data-testid="admin-users-card">
      <h2 className="text-lg font-semibold mb-1 text-brand-primary flex items-center gap-2"><Users size={18}/>Team / admin users</h2>
      <p className="text-xs text-slate-500 mb-4">Anyone in this list can sign in with their email via magic-link and access the admin dashboard. Env-set admins are locked (change them by updating <code className="bg-slate-100 px-1 rounded">ADMIN_EMAILS</code> and redeploying).</p>

      <form onSubmit={add} className="space-y-3 mb-5">
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label className="text-slate-800 text-sm">Invite new admin by email</Label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
              <Input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="alex@example.com"
                className="pl-9"
                disabled={adding}
                required
                data-testid="add-admin-email-input"
              />
            </div>
          </div>
          <Button type="submit" disabled={adding || !addEmail.trim()} style={{ background: "#00C7FF", color: "#0F1644" }} className="font-semibold" data-testid="add-admin-btn">
            {adding ? <Loader2 size={14} className="animate-spin"/> : <><UserPlus size={14} className="mr-1.5"/>Add admin</>}
          </Button>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 select-none cursor-pointer" data-testid="send-invite-toggle">
          <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} className="h-3.5 w-3.5 accent-[#00C7FF]"/>
          Send invitation email with sign-in instructions
        </label>
      </form>

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin"/>Loading…</div>
      ) : (
        <div className="space-y-1" data-testid="admin-users-list">
          {data.from_env.map(u => (
            <div key={`env-${u.email}`} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-slate-50 border border-slate-200" data-testid={`admin-row-${u.email}`}>
              <div className="min-w-0 flex items-center gap-2 truncate">
                <Lock size={12} className="text-slate-500 shrink-0"/>
                <span className="text-sm text-slate-900 truncate">{u.email}</span>
                {u.email === data.current && <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">you</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resend(u.email)}
                  disabled={resendingEmail === u.email}
                  className="text-slate-600 hover:bg-slate-100 h-7 px-2"
                  data-testid={`resend-admin-${u.email}`}
                  title="Send a fresh sign-in invitation"
                >
                  {resendingEmail === u.email ? <Loader2 size={12} className="animate-spin"/> : <><Send size={11} className="mr-1"/>Resend</>}
                </Button>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-300 rounded px-1.5 py-0.5">env</span>
              </div>
            </div>
          ))}
          {data.from_db.map(u => (
            <div key={`db-${u.email}`} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-white border border-slate-200 hover:bg-slate-50 transition" data-testid={`admin-row-${u.email}`}>
              <div className="min-w-0 flex items-center gap-2 truncate">
                <Mail size={12} className="text-slate-500 shrink-0"/>
                <span className="text-sm text-slate-900 truncate">{u.email}</span>
                {u.email === data.current && <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">you</span>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resend(u.email)}
                  disabled={resendingEmail === u.email}
                  className="text-slate-600 hover:bg-slate-100 h-7 px-2"
                  data-testid={`resend-admin-${u.email}`}
                  title="Send a fresh sign-in invitation"
                >
                  {resendingEmail === u.email ? <Loader2 size={12} className="animate-spin"/> : <><Send size={11} className="mr-1"/>Resend</>}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPendingRemove(u.email)}
                  disabled={removingEmail === u.email}
                  className="text-rose-600 hover:bg-rose-50 h-7 px-2"
                  data-testid={`remove-admin-${u.email}`}
                  title="Remove this admin"
                >
                  {removingEmail === u.email ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
                </Button>
              </div>
            </div>
          ))}
          {data.from_env.length + data.from_db.length === 0 && (
            <p className="text-sm text-slate-500 italic">No admins yet — add one above.</p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingRemove}
        onOpenChange={(v) => !v && setPendingRemove(null)}
        title="Remove admin?"
        description={pendingRemove ? `Remove ${pendingRemove} from the admin allowlist? They'll lose access on next magic-link sign-in. Any active session remains valid until the JWT expires.` : ""}
        confirmLabel="Remove admin"
        destructive
        onConfirm={() => pendingRemove && remove(pendingRemove)}
        testId="remove-admin-confirm"
      />
    </Card>
  );
}
